import type { LucideIcon } from 'lucide-react';
import { User, Settings, Lightbulb, Star, FileSignature, FileText, Users, Car, Shield, CreditCard, Wallet } from 'lucide-react';

export interface NavCard {
  to: string;
  label: string;
  subtitle: string;
  icon: LucideIcon;
  color: string;
  bg: string;
  visible: (flags: { isCarrier: boolean; isWorkshop: boolean }) => boolean;
}

// Single source of truth for the "my account" pages surfaced both in the NavBar's profile
// dropdown (App.tsx) and the Dashboard's Quick Links section (UserDashboardPage.tsx), so the two
// stay in sync instead of drifting apart. Mirrors the mobile app's navCards.ts.
export const NAV_CARDS: NavCard[] = [
  {
    to: '/profile', label: 'My Profile', subtitle: 'Personal & business details',
    icon: User, color: '#2563eb', bg: 'bg-blue-50', visible: () => true,
  },
  {
    to: '/preferences', label: 'My Preferences', subtitle: 'Voice assistant & job alerts',
    icon: Settings, color: '#4f46e5', bg: 'bg-indigo-50', visible: () => true,
  },
  {
    to: '/wallet', label: 'My Wallet', subtitle: 'Balance, top-ups & statement',
    icon: Wallet, color: '#7c3aed', bg: 'bg-violet-50', visible: () => true,
  },
  {
    to: '/invoices', label: 'Invoices', subtitle: 'Create, view & download invoices',
    icon: FileText, color: '#0f766e', bg: 'bg-teal-50', visible: () => true,
  },
  {
    to: '/my-contracts', label: 'My Contracts', subtitle: 'Filter and download contract PDFs',
    icon: FileSignature, color: '#0369a1', bg: 'bg-sky-50', visible: () => true,
  },
  {
    to: '/payouts', label: 'Payout Setup', subtitle: 'Stripe Connect & bank account',
    icon: CreditCard, color: '#059669', bg: 'bg-emerald-50', visible: () => true,
  },
  {
    to: '/suggestions', label: 'Suggestions', subtitle: 'Share feedback & feature ideas',
    icon: Lightbulb, color: '#2563eb', bg: 'bg-blue-50', visible: () => true,
  },
  {
    to: '/my-reviews', label: 'My Reviews', subtitle: "Ratings & reviews you've received",
    icon: Star, color: '#d97706', bg: 'bg-amber-50', visible: () => true,
  },
  {
    to: '/my-ratings', label: 'My Ratings', subtitle: 'Upload external platform ratings',
    icon: Star, color: '#d97706', bg: 'bg-amber-50',
    visible: ({ isCarrier, isWorkshop }) => isCarrier || isWorkshop,
  },
  {
    to: '/employees', label: 'Employees', subtitle: 'Team members & magic links',
    icon: Users, color: '#0891b2', bg: 'bg-cyan-50', visible: ({ isCarrier }) => isCarrier,
  },
  {
    // Visible to every role: Customers and Workshops own vehicles and get MOT reminders too.
    to: '/vehicles', label: 'Vehicles', subtitle: 'MOT, insurance & history',
    icon: Car, color: '#7c3aed', bg: 'bg-violet-50', visible: () => true,
  },
  {
    to: '/documents', label: 'Insurance Docs', subtitle: 'Business insurance uploads',
    icon: Shield, color: '#4b5563', bg: 'bg-gray-100', visible: ({ isWorkshop }) => isWorkshop,
  },
];

export function visibleNavCards(flags: { isCarrier: boolean; isWorkshop: boolean }): NavCard[] {
  return NAV_CARDS.filter(card => card.visible(flags));
}
